import React, { useState } from "react";
import { ChevronDown } from "lucide-react";
import SearchableCheckboxList from "@/components/Core/SearchableCheckboxList";
import { cn } from "@/utils/cn";

/**
 * Toggle a value in a string-array group of a filter definition, returning a new
 * definition with the group cleared to `undefined` when it becomes empty. Domain
 * agnostic — works for any definition whose group is a `string[]`.
 */
export function toggleInGroup<T extends Record<string, any>>(
	def: T,
	key: keyof T,
	value: string
): T {
	const current = (def[key] as string[] | undefined) ?? [];
	const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
	return { ...def, [key]: next.length ? next : undefined };
}

/**
 * A titled, collapsible group of filter controls. Used across every filter
 * builder (consultant / customer / job), so collapse + the active-value count
 * badge are shared chrome rather than per-domain.
 *
 * - `count`: when > 0, shown as "(n)" next to the title and auto-expands the
 *   section on first render so active constraints are visible.
 * - `collapsible` (default true): when false, renders a static titled block.
 * - `scrollable` (default false): caps the content at a fixed height with its
 *   own scroll — only long option lists want this; custom inputs/switches need
 *   the full height so they aren't clipped.
 * - `flush` (default false): drops the horizontal padding so the section lines
 *   up with unpadded neighbours (e.g. a form column rather than a sheet).
 */
export const Section: React.FC<{
	title: string;
	children: React.ReactNode;
	divider?: boolean;
	count?: number;
	collapsible?: boolean;
	defaultExpanded?: boolean;
	scrollable?: boolean;
	flush?: boolean;
}> = ({
	title,
	children,
	divider,
	count = 0,
	collapsible = true,
	defaultExpanded = false,
	scrollable = false,
	flush = false,
}) => {
	const [expanded, setExpanded] = useState(() => defaultExpanded || count > 0);

	const heading = (
		<span className="text-base font-medium text-gray-800">
			{title}
			{count > 0 && <span className="font-normal text-gray-500"> ({count})</span>}
		</span>
	);

	const content = (
		<div className={cn("flex flex-col gap-2", scrollable && "max-h-48 overflow-auto")}>
			{children}
		</div>
	);

	if (!collapsible) {
		return (
			<div className={cn("mb-5", divider && "border-t border-solid border-gray-200 pt-5")}>
				<div className="mb-2">{heading}</div>
				{content}
			</div>
		);
	}

	return (
		<div className={cn(divider && "border-t border-solid border-gray-200")}>
			<button
				type="button"
				onClick={() => setExpanded((prev) => !prev)}
				className={cn(
					"flex w-full cursor-pointer items-center justify-between border-0 bg-transparent py-3 text-left",
					!flush && "px-5"
				)}
			>
				{heading}
				<ChevronDown
					className={cn(
						"h-4 w-4 shrink-0 text-gray-500 transition-transform",
						expanded && "rotate-180"
					)}
				/>
			</button>
			{expanded && <div className={cn("pb-3", !flush && "px-5")}>{content}</div>}
		</div>
	);
};

export interface CheckboxOption {
	value: string;
	label: string;
}

/**
 * A `Section` of multi-select checkboxes backed by a string-array group. Renders
 * nothing when there are no options. The count badge / collapse come from
 * `Section`; the active count is derived from `selected`. The repeated
 * gender/manager/org-unit/pool pattern across the filter builders, in one place.
 *
 * Long lists (customers, contracts…) get a search box on top via
 * `SearchableCheckboxList`, which also handles the list's own scroll — so this
 * `Section` is not `scrollable` itself (the search box must stay pinned above
 * the scroll region). `searchThreshold` is forwarded so callers can tune when
 * the search appears.
 *
 * `extraOptions` holds second-class values (deactivated consultant managers and
 * the like) behind a subtle toggle link, so the default list stays short.
 * Selected extras are always listed — an active constraint must never be
 * invisible.
 */
export const CheckboxListSection: React.FC<{
	title: string;
	options: CheckboxOption[];
	selected?: string[];
	onToggle: (value: string) => void;
	divider?: boolean;
	searchThreshold?: number;
	/** Options hidden until the user asks for them; appended after `options`. */
	extraOptions?: CheckboxOption[];
	/** Link text revealing `extraOptions`. Without it the extras stay hidden. */
	showExtraLabel?: string;
	/** Link text hiding them again. Falls back to `showExtraLabel`. */
	hideExtraLabel?: string;
}> = ({
	title,
	options,
	selected,
	onToggle,
	divider,
	searchThreshold,
	extraOptions,
	showExtraLabel,
	hideExtraLabel,
}) => {
	const [showExtra, setShowExtra] = useState(false);

	const extras = extraOptions ?? [];
	// With nothing in the primary list there is nothing to keep short — show the
	// extras outright rather than an empty section behind a link.
	const canToggleExtras = !!extras.length && !!showExtraLabel && !!options.length;
	const visible =
		canToggleExtras && !showExtra
			? [...options, ...extras.filter((o) => selected?.includes(o.value))]
			: [...options, ...extras];

	if (!visible.length) return null;

	return (
		<Section title={title} divider={divider} count={selected?.length ?? 0}>
			<SearchableCheckboxList
				options={visible}
				selected={selected}
				onToggle={onToggle}
				searchThreshold={searchThreshold}
			/>
			{canToggleExtras && (
				<button
					type="button"
					onClick={() => setShowExtra((prev) => !prev)}
					className="cursor-pointer self-start border-0 bg-transparent p-0 text-sm text-gray-500 underline hover:text-gray-700"
				>
					{showExtra ? hideExtraLabel ?? showExtraLabel : showExtraLabel}
				</button>
			)}
		</Section>
	);
};
